import type { FetchArgs } from "@reduxjs/toolkit/query";

export interface DownloadedFile {
	blob: Blob;
	filename: string;
}

export const FINANCIAL_DOWNLOAD_TOAST = {
	sample: "Sample template downloaded successfully.",
	allUnits: "All units exported.",
	pendingUnits: "Pending units exported.",
	unitPdf: "PDF downloaded successfully.",
} as const;

export function invoiceCsvDownloadToast(invoiceNumber?: string): string {
	const number = invoiceNumber?.trim();
	if (!number || number === "—") return "Invoice exported as CSV.";
	return `Invoice ${number} exported as CSV.`;
}

export function parseDownloadedFilename(
	disposition: string | null,
	fallback: string,
): string {
	if (!disposition) return fallback;
	const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
	if (utfMatch?.[1]) {
		try {
			return decodeURIComponent(utfMatch[1].trim());
		} catch {
			return utfMatch[1].trim();
		}
	}
	const basicMatch = disposition.match(/filename="?([^"]+)"?/i);
	return basicMatch?.[1]?.trim() || fallback;
}

export function triggerBrowserDownload(file: DownloadedFile): void {
	const objectUrl = URL.createObjectURL(file.blob);
	const link = document.createElement("a");
	link.href = objectUrl;
	link.download = file.filename;
	document.body.append(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(objectUrl);
}

function isDownloadedFile(value: unknown): value is DownloadedFile {
	return (
		!!value &&
		typeof value === "object" &&
		"blob" in value &&
		(value as DownloadedFile).blob instanceof Blob
	);
}

async function parseDownloadBody(
	response: Response,
	fallbackFilename: string,
): Promise<DownloadedFile | Record<string, unknown>> {
	const contentType = response.headers.get("content-type") ?? "";
	const looksLikeJson =
		contentType.includes("application/json") ||
		contentType.includes("text/json");
	if (!response.ok || looksLikeJson) {
		const text = await response.text();
		if (!text.trim()) {
			return { message: `Download failed (${response.status}).` };
		}
		try {
			const parsed = JSON.parse(text) as unknown;
			if (parsed && typeof parsed === "object") {
				return parsed as Record<string, unknown>;
			}
			return { message: String(parsed) };
		} catch {
			return { message: text.trim() };
		}
	}
	const blob = await response.blob();
	return {
		blob,
		filename: parseDownloadedFilename(
			response.headers.get("content-disposition"),
			fallbackFilename,
		),
	};
}

export function blobDownloadQuery(
	url: string,
	fallbackFilename: string,
	params?: Record<string, string>,
): FetchArgs {
	return {
		url,
		method: "GET",
		headers: { accept: "*/*" },
		cache: "no-store",
		params,
		responseHandler: (response: Response) =>
			parseDownloadBody(response, fallbackFilename),
		validateStatus: (_response, body) => isDownloadedFile(body),
	};
}
